import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import { useTheme } from "@mui/material/styles";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { API_ENDPOINTS } from "../apiConfig";
import { useMatchDetailsContext } from "../components/matchDetails/MatchDetailsContext";
import ScheduleTable from "../components/ScheduleTable";
import { useSeason } from "../components/SeasonContext";
import { TeamScheduleProvider } from "../components/TeamScheduleContext";
import { Matchday } from "../interfaces/Match";
import { Team } from "../interfaces/Team";

interface ScheduleSectionProps {
  adminView: boolean;
}

const ScheduleSection = ({ adminView = false }: ScheduleSectionProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [selected, setSelected] = useState<number>(0);
  const [hasUserSelected, setHasUserSelected] = useState<boolean>(false);
  const [spieltagData, setSpieltagData] = useState<Matchday[]>([]);
  const [teamData, setTeamData] = useState<Team[]>([]);
  const [searchParams] = useSearchParams();

  // Loading states
  const [isLoadingInitialData, setIsLoadingInitialData] = useState(false);
  const [isLoadingTeamMatches, setIsLoadingTeamMatches] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  const { selectedSeason } = useSeason();
  const { setMatchId, matchId } = useMatchDetailsContext();

  // Check if all loading is complete
  const isLoading =
    isLoadingInitialData || isLoadingTeamMatches || !hasInitialized;

  // Get view mode from URL params, default to 'matchday'
  const viewMode =
    searchParams.get("displayOption") === "team" ? "team" : "matchday";

  const now = new Date()

  // Restore selected from localStorage on mount/season change
  useEffect(() => {
    if (!selectedSeason) return;
    // Only set the initial selection if the user hasn't selected manually
    if (!hasUserSelected) {
      let sel = 0;
      if (spieltagData.length > 0) {
        const nowTime = now.getTime();
        let minDiff = Infinity;
        let closestIdx = 0;
        spieltagData.forEach((md, idx) => {
          if (md.date) {
            const mdTime = new Date(md.date).getTime();
            const diff = Math.abs(mdTime - nowTime);
            if (diff < minDiff) {
              minDiff = diff;
              closestIdx = idx;
            }
          }
        });
        sel = closestIdx;
      }
      setSelected(sel);
      // Always set matchId to first match of selected gameday for admin
      if (adminView) {
        setTimeout(() => {
          const firstMatch = spieltagData[sel]?.matches?.[0]?.id;
          if (firstMatch && typeof firstMatch === "number") {
            setMatchId(firstMatch);
          }
          setHasInitialized(true);
        }, 0);
      } else {
        setHasInitialized(true);
      }
    }
  }, [selectedSeason, adminView, spieltagData, setMatchId, now, hasUserSelected]);

  // Auto-select first match for admin if none selected
  useEffect(() => {
    if (!adminView) return;
    if (!spieltagData.length) return;
    const firstMatch = spieltagData[0]?.matches?.[0]?.id;
    if (
      firstMatch &&
      typeof firstMatch === "number" &&
      (!matchId || matchId < 1)
    ) {
      setMatchId(firstMatch);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminView, spieltagData, matchId]);

  const handleTabChange = (newValue: number) => {
    setSelected(newValue);
    setHasUserSelected(true);
    if (adminView) {
      const spieltag = spieltagData[newValue];
      const firstMatch = spieltag?.matches?.[0]?.id;
      if (firstMatch && typeof firstMatch === "number") {
        setMatchId(firstMatch);
      } else {
        setMatchId(-1);
      }
    }
  };

  // Fetch schedule/team data when selectedSeason changes
  useEffect(() => {
    if (!selectedSeason) return;

    setIsLoadingInitialData(true);
    setHasInitialized(false);

    const fetchData = async () => {
      try {
        const [matchdayRes, teamRes] = await Promise.all([
          fetch(API_ENDPOINTS.matchesByMatchday(selectedSeason)),
          fetch(API_ENDPOINTS.teams),
        ]);
        const matchdayText = await matchdayRes.text();
        const teamText = await teamRes.text();
        let matchdayData: Matchday[], teamData: Team[];
        try {
          matchdayData = JSON.parse(matchdayText);
          teamData = JSON.parse(teamText);
        } catch (e) {
          console.error("Failed to parse matchday JSON:", e);
          throw new Error("Matchday endpoint did not return valid JSON");
        }
        setSpieltagData(matchdayData);
        setTeamData(teamData);
        // Don't reset selected here; it's handled by the effect above
      } finally {
        setIsLoadingInitialData(false);
      }
    };

    fetchData();
  }, [selectedSeason]);

  // Fetch team matches when a team is selected in team view
  useEffect(() => {
    if (
      viewMode !== "team" ||
      !Array.isArray(teamData) ||
      teamData.length === 0 ||
      selected < 0 ||
      selected >= teamData.length ||
      !selectedSeason
    )
      return;
    const selectedTeam = teamData[selected];
    if (
      selectedTeam.matches &&
      Array.isArray(selectedTeam.matches) &&
      selectedTeam.matches.length > 0
    ) {
      return;
    }

    setIsLoadingTeamMatches(true);

    const fetchTeamMatches = async () => {
      try {
        const teamId = selectedTeam.id;
        const res = await fetch(
          API_ENDPOINTS.matchesByTeam(selectedSeason, teamId)
        );
        const data = await res.json();
        setTeamData((prev) => {
          const updated = [...prev];
          updated[selected] = { ...updated[selected], matches: data.matches };
          return updated;
        });
      } finally {
        setIsLoadingTeamMatches(false);
      }
    };

    fetchTeamMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [viewMode, selected, selectedSeason]);

  // For matchday view
  const matchdayRows = spieltagData[selected]?.matches || [];

  return (
    <Box sx={{ padding: 0, margin: 0 }}>
      {isLoading && (
        <Box sx={{ width: "100%", mb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Lade Spieltag...
          </Typography>
          <LinearProgress />
        </Box>
      )}
      {viewMode === "matchday" && (
        <Tabs
          value={selected}
          onChange={(_, idx) => handleTabChange(idx)}
          variant="scrollable"
          scrollButtons="auto"
          // sx={{ mb: 2, minHeight: 36 }}
        >
          {spieltagData.map((spieltag, idx) => (
            <Tab
              key={spieltag.match_day || `Spieltag${idx + 1}`}
              label={
                typeof spieltag.match_day === "number"
                  ? `Spieltag ${spieltag.match_day}`
                  : `Spieltag ${idx + 1}`
              }
              sx={{
                fontSize: "0.75rem",
                lineHeight: 1.2,
                minHeight: "auto",
                textTransform: "none",
                whiteSpace: "normal",
                wordWrap: "break-word",
                maxWidth: 120,
              }}
            />
          ))}
        </Tabs>
      )}
      {viewMode === "team" &&
        Array.isArray(teamData) &&
        teamData.length > 0 && (
          <Tabs
            value={selected}
            onChange={(_, idx) => handleTabChange(idx)}
            variant="scrollable"
            scrollButtons="auto"
            visibleScrollbar={isMobile}
          >
            {teamData.map((team) => (
              <Tab
                key={team.id}
                label={
                  typeof team.name === "string" ? team.name : String(team.id)
                }
                sx={{
                  fontSize: "0.75rem",
                  lineHeight: 1.2,
                  minHeight: "auto",
                  textTransform: "none",
                  whiteSpace: "normal",
                  wordWrap: "break-word",
                  maxWidth: 120,
                }}
              />
            ))}
          </Tabs>
        )}
      {/* Team schedule table with view toggles and PDF download */}
      {viewMode === "team" &&
        Array.isArray(teamData) &&
        teamData.length > 0 &&
        selected >= 0 &&
        selected < teamData.length &&
        teamData[selected] && (
          <TeamScheduleProvider team={teamData[selected]}>
            <ScheduleTable
              matches={teamData[selected].matches || []}
              isMobile={isMobile}
              showScores={!adminView}
              showViewToggles={true}
              showDownloadButton={true}
              adminView={adminView}
            />
          </TeamScheduleProvider>
        )}
      {viewMode === "matchday" && (
        <ScheduleTable
          matches={matchdayRows}
          isMobile={!isMobile}
          showScores={!adminView}
          showViewToggles={false}
          showDownloadButton={false}
          adminView={adminView}
        />
      )}
    </Box>
  );
};

export default ScheduleSection;
